/**
 * Generator Form Component
 * Main form for generating bank statements with searchable template dropdown
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { pdfTemplateAPI, generationAPI } from '../../services/api';

interface Template {
  id: string;
  identifier: any;
  layoutType: string;
  paperSize: string;
  styleProfile?: {
    fontFamily: string;
    baseFontSize: number;
    primaryColor: string;
    secondaryColor: string;
  };
  previewImageUrl?: string;
  thumbnailPreviewSvg?: string;
  learnedFromSample?: boolean;
  createdAt: string;
}

interface EmployerSchedule {
  employerName: string;
  baseNetPay: number;
  payFrequency: 'WEEKLY' | 'BIWEEKLY' | 'SEMIMONTHLY' | 'MONTHLY';
  firstPayDate: string;
  randomVariationPercent: number;
}

type RecurringPaymentType = 'RENT' | 'MORTGAGE' | 'CAR_PAYMENT' | 'CAR_LEASE' | 'INSURANCE' | 'UTILITIES' | 'SUBSCRIPTION' | 'OTHER';
type PaymentFrequency = 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';

interface RecurringPayment {
  id: string;
  paymentType: RecurringPaymentType;
  payeeName: string;
  amount: number;
  frequency: PaymentFrequency;
  dayOfMonth: number;
}

const PAYMENT_TYPE_OPTIONS: { value: RecurringPaymentType; label: string; defaultPayee: string }[] = [
  { value: 'RENT', label: 'Rent', defaultPayee: 'Property Management LLC' },
  { value: 'MORTGAGE', label: 'Mortgage', defaultPayee: 'Wells Fargo Mortgage' },
  { value: 'CAR_PAYMENT', label: 'Car Payment', defaultPayee: 'Toyota Financial Services' },
  { value: 'CAR_LEASE', label: 'Car Lease', defaultPayee: 'Honda Financial Services' },
  { value: 'INSURANCE', label: 'Insurance', defaultPayee: 'State Farm Insurance' },
  { value: 'UTILITIES', label: 'Utilities', defaultPayee: 'City Power & Light' },
  { value: 'SUBSCRIPTION', label: 'Subscription', defaultPayee: 'Netflix' },
  { value: 'OTHER', label: 'Other', defaultPayee: '' },
];

interface GeneratorFormProps {
  onGenerationSuccess?: (result: any) => void;
  initialTemplateId?: string;
}

export const GeneratorForm: React.FC<GeneratorFormProps> = ({ onGenerationSuccess, initialTemplateId }) => {
  // Template selection
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Account Information
  const [accountNumber, setAccountNumber] = useState('');

  // Customer Information
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Address Information
  const [streetNumber, setStreetNumber] = useState('');
  const [streetName, setStreetName] = useState('');
  const [apartmentUnit, setApartmentUnit] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');

  // Statement Settings
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startingBalance, setStartingBalance] = useState('1500.00');
  const [transactionsPerMonth, setTransactionsPerMonth] = useState(30);
  const [debitIntensity, setDebitIntensity] = useState<'LOW' | 'MEDIUM' | 'HIGH'>('MEDIUM');

  const [formats, setFormats] = useState({ pdf: true, html: true, excel: true });

  // Employer schedule
  const [includeEmployer, setIncludeEmployer] = useState(false);
  const [employerSchedule, setEmployerSchedule] = useState<EmployerSchedule>({
    employerName: '',
    baseNetPay: 3000,
    payFrequency: 'BIWEEKLY',
    firstPayDate: '',
    randomVariationPercent: 5
  });

  // Recurring payments
  const [recurringPayments, setRecurringPayments] = useState<RecurringPayment[]>([]);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadTemplates();
    // Set default dates (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
    setEmployerSchedule(prev => ({
      ...prev,
      firstPayDate: firstDay.toISOString().split('T')[0]
    }));
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await pdfTemplateAPI.getTemplates() as any;
      const templateList = response.templates || response;
      // Sort alphabetically
      const sorted = templateList.sort((a: any, b: any) => {
        const nameA = getBankDisplayName(a).toLowerCase();
        const nameB = getBankDisplayName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setTemplates(sorted);

      // If initialTemplateId is provided, select that template
      if (initialTemplateId) {
        const initialTemplate = sorted.find((t: Template) => t.id === initialTemplateId);
        if (initialTemplate) {
          setSelectedTemplate(initialTemplate);
          setSearchQuery(getBankDisplayName(initialTemplate));
        }
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const getBankDisplayName = (template: any): string => {
    // Handle PDF templates (from pdf-templates API)
    if (template?.templateName) {
      return template.templateName;
    }
    if (template?.bankName) {
      return template.bankName;
    }
    // Handle legacy templates (from templates API)
    if (!template?.identifier) return 'Unknown Bank';
    if (typeof template.identifier === 'object' && !Array.isArray(template.identifier)) {
      return template.identifier.bankDisplayName || template.identifier.userFriendlyName || 'Unknown Bank';
    }
    if (Array.isArray(template.identifier)) {
      return template.identifier[0] || 'Unknown Bank';
    }
    return 'Unknown Bank';
  };

  const getBankCode = (template: any): string | null => {
    if (!template?.identifier) return null;
    if (typeof template.identifier === 'object' && !Array.isArray(template.identifier)) {
      return template.identifier.bankCode || null;
    }
    if (Array.isArray(template.identifier) && template.identifier.length > 1) {
      return template.identifier[1];
    }
    return null;
  };

  // Filter templates based on search query
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(t => {
      const bankName = getBankDisplayName(t).toLowerCase();
      const bankCode = getBankCode(t)?.toLowerCase() || '';
      return bankName.includes(query) || bankCode.includes(query);
    });
  }, [templates, searchQuery]);

  const handleSelectTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setSearchQuery(getBankDisplayName(template));
    setIsDropdownOpen(false);
  };

  // Build full account holder name
  const getFullName = () => {
    return `${firstName} ${lastName}`.trim();
  };

  // Build full address
  const getFullAddress = () => {
    let address = '';
    if (streetNumber) address += streetNumber + ' ';
    if (streetName) address += streetName;
    if (apartmentUnit) address += ', ' + apartmentUnit;
    return address.trim();
  };

  // Recurring payment handlers
  const addRecurringPayment = () => {
    const newPayment: RecurringPayment = {
      id: Date.now().toString(),
      paymentType: 'RENT',
      payeeName: 'Property Management LLC',
      amount: 1500,
      frequency: 'MONTHLY',
      dayOfMonth: 1,
    };
    setRecurringPayments([...recurringPayments, newPayment]);
  };

  const updateRecurringPayment = (id: string, field: keyof RecurringPayment, value: any) => {
    setRecurringPayments(payments =>
      payments.map(p => {
        if (p.id === id) {
          const updated = { ...p, [field]: value };
          // Auto-update payee name when type changes
          if (field === 'paymentType') {
            const typeOption = PAYMENT_TYPE_OPTIONS.find(opt => opt.value === value);
            if (typeOption && typeOption.defaultPayee) {
              updated.payeeName = typeOption.defaultPayee;
            }
          }
          return updated;
        }
        return p;
      })
    );
  };

  const removeRecurringPayment = (id: string) => {
    setRecurringPayments(payments => payments.filter(p => p.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTemplate) {
      setError('Please select a template');
      return;
    }

    if (!firstName || !lastName) {
      setError('Please enter customer first and last name');
      return;
    }

    if (!formats.pdf && !formats.html && !formats.excel) {
      setError('Please select at least one output format');
      return;
    }

    try {
      setGenerating(true);
      setError(null);

      const request = {
        requestedTemplateId: selectedTemplate.id,
        accountNumberMasked: accountNumber,
        accountHolderName: getFullName(),
        bankName: getBankDisplayName(selectedTemplate),
        currency: 'USD',
        startDate,
        endDate,
        initialOpeningBalance: parseFloat(startingBalance),
        formats: formats,
        employerSchedules: includeEmployer ? [employerSchedule] : [],
        recurringPayments: recurringPayments,
        transactionsPerMonth,
        debitIntensity,
        // Extended customer info
        customerInfo: {
          firstName,
          lastName,
          streetNumber,
          streetName,
          apartmentUnit,
          city,
          state,
          zipCode,
          fullAddress: getFullAddress(),
        },
        locationContext: { city, region: state, country: 'USA' },
      };

      // Use pdfTemplateAPI for PDF templates
      const response = await pdfTemplateAPI.generateStatement(selectedTemplate.id, request);

      if (onGenerationSuccess) {
        onGenerationSuccess(response);
      }
    } catch (err: any) {
      setError(err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // Cache-busting version for preview images
  const cacheBuster = 'v3';

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-8">
      {/* Template Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">1. Select Bank Template</h2>

        <div ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search and Select Bank ({templates.length} available)
          </label>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsDropdownOpen(true);
                if (!e.target.value) setSelectedTemplate(null);
              }}
              onFocus={() => setIsDropdownOpen(true)}
              placeholder="Type to search banks..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Dropdown List */}
            {isDropdownOpen && !selectedTemplate && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                {filteredTemplates.length === 0 ? (
                  <div className="px-4 py-3 text-gray-500">No matching templates found</div>
                ) : (
                  filteredTemplates.map((template) => {
                    const bankName = getBankDisplayName(template);
                    const bankCode = getBankCode(template);
                    return (
                      <div
                        key={template.id}
                        onClick={() => handleSelectTemplate(template)}
                        className={`px-4 py-3 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-0 flex items-center gap-3 ${
                          selectedTemplate?.id === template.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        {/* Preview thumbnail */}
                        {template.previewImageUrl ? (
                          <img
                            src={template.previewImageUrl + '?' + cacheBuster}
                            alt={bankName}
                            className="w-12 h-16 object-cover rounded border border-gray-200"
                          />
                        ) : template.styleProfile?.primaryColor ? (
                          <div
                            className="w-12 h-16 rounded border border-gray-200 flex items-center justify-center"
                            style={{ backgroundColor: template.styleProfile.primaryColor + '20' }}
                          >
                            <div
                              className="w-6 h-6 rounded"
                              style={{ backgroundColor: template.styleProfile.primaryColor }}
                            ></div>
                          </div>
                        ) : (
                          <div className="w-12 h-16 rounded border border-gray-200 bg-gray-100"></div>
                        )}
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{bankName}</div>
                          <div className="text-sm text-gray-500">
                            {bankCode && <span className="mr-3">Code: {bankCode}</span>}
                            <span>{(template.layoutType || 'PDF Template').replace(/_/g, ' ')}</span>
                          </div>
                        </div>
                        {selectedTemplate?.id === template.id && (
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Selected Template Preview */}
          {selectedTemplate && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-start gap-4">
                {selectedTemplate.previewImageUrl ? (
                  <img
                    src={selectedTemplate.previewImageUrl + '?' + cacheBuster}
                    alt={getBankDisplayName(selectedTemplate)}
                    className="w-32 h-auto max-h-48 object-contain rounded border border-gray-300 shadow-sm bg-white"
                  />
                ) : (
                  <div className="w-32 h-40 rounded border border-gray-300 bg-white flex items-center justify-center">
                    <span className="text-gray-400 text-xs text-center px-2">No preview</span>
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 text-lg">{getBankDisplayName(selectedTemplate)}</h3>
                  <div className="text-sm text-gray-600 mt-2 space-y-1">
                    <p><span className="font-medium">Layout:</span> {(selectedTemplate.layoutType || 'PDF Template').replace(/_/g, ' ')}</p>
                    <p><span className="font-medium">Paper:</span> {(selectedTemplate.paperSize || 'Letter').replace(/_/g, ' ')}</p>
                    {selectedTemplate.styleProfile && (
                      <p className="flex items-center gap-2">
                        <span className="font-medium">Color:</span>
                        <span
                          className="inline-block w-4 h-4 rounded border border-gray-300"
                          style={{ backgroundColor: selectedTemplate.styleProfile.primaryColor }}
                        ></span>
                        {selectedTemplate.styleProfile.primaryColor}
                      </p>
                    )}
                    {selectedTemplate.learnedFromSample && (
                      <span className="inline-block mt-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        AI Learned Template
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedTemplate(null);
                    setSearchQuery('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Account & Customer Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">2. Account & Customer Information</h2>

        {/* Account Details */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 border-b pb-2">Account Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number *
              </label>
              <input
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder="1234567890"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Customer Name */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 border-b pb-2">Customer Name</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 border-b pb-2">Customer Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Number *
              </label>
              <input
                type="text"
                value={streetNumber}
                onChange={(e) => setStreetNumber(e.target.value)}
                placeholder="123"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Name *
              </label>
              <input
                type="text"
                value={streetName}
                onChange={(e) => setStreetName(e.target.value)}
                placeholder="Main Street"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apt/Unit
              </label>
              <input
                type="text"
                value={apartmentUnit}
                onChange={(e) => setApartmentUnit(e.target.value)}
                placeholder="Apt 4B"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City *
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="New York"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State *
              </label>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select State</option>
                <option value="AL">Alabama</option>
                <option value="AK">Alaska</option>
                <option value="AZ">Arizona</option>
                <option value="AR">Arkansas</option>
                <option value="CA">California</option>
                <option value="CO">Colorado</option>
                <option value="CT">Connecticut</option>
                <option value="DE">Delaware</option>
                <option value="FL">Florida</option>
                <option value="GA">Georgia</option>
                <option value="HI">Hawaii</option>
                <option value="ID">Idaho</option>
                <option value="IL">Illinois</option>
                <option value="IN">Indiana</option>
                <option value="IA">Iowa</option>
                <option value="KS">Kansas</option>
                <option value="KY">Kentucky</option>
                <option value="LA">Louisiana</option>
                <option value="ME">Maine</option>
                <option value="MD">Maryland</option>
                <option value="MA">Massachusetts</option>
                <option value="MI">Michigan</option>
                <option value="MN">Minnesota</option>
                <option value="MS">Mississippi</option>
                <option value="MO">Missouri</option>
                <option value="MT">Montana</option>
                <option value="NE">Nebraska</option>
                <option value="NV">Nevada</option>
                <option value="NH">New Hampshire</option>
                <option value="NJ">New Jersey</option>
                <option value="NM">New Mexico</option>
                <option value="NY">New York</option>
                <option value="NC">North Carolina</option>
                <option value="ND">North Dakota</option>
                <option value="OH">Ohio</option>
                <option value="OK">Oklahoma</option>
                <option value="OR">Oregon</option>
                <option value="PA">Pennsylvania</option>
                <option value="RI">Rhode Island</option>
                <option value="SC">South Carolina</option>
                <option value="SD">South Dakota</option>
                <option value="TN">Tennessee</option>
                <option value="TX">Texas</option>
                <option value="UT">Utah</option>
                <option value="VT">Vermont</option>
                <option value="VA">Virginia</option>
                <option value="WA">Washington</option>
                <option value="WV">West Virginia</option>
                <option value="WI">Wisconsin</option>
                <option value="WY">Wyoming</option>
                <option value="DC">District of Columbia</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP Code *
              </label>
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder="10001"
                maxLength={10}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Statement Settings */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">3. Statement Settings</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date *
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setEmployerSchedule(prev => ({ ...prev, firstPayDate: e.target.value }));
              }}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date *
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Starting Balance *
            </label>
            <input
              type="number"
              step="0.01"
              value={startingBalance}
              onChange={(e) => setStartingBalance(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transactions Per Month
            </label>
            <input
              type="number"
              value={transactionsPerMonth}
              onChange={(e) => setTransactionsPerMonth(parseInt(e.target.value))}
              min="5"
              max="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Spending Intensity
            </label>
            <select
              value={debitIntensity}
              onChange={(e) => setDebitIntensity(e.target.value as 'LOW' | 'MEDIUM' | 'HIGH')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="LOW">Low (smaller amounts)</option>
              <option value="MEDIUM">Medium (moderate amounts)</option>
              <option value="HIGH">High (larger amounts)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Employer Schedule (Optional) */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">4. Employer Schedule (Optional)</h2>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeEmployer}
              onChange={(e) => setIncludeEmployer(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-sm font-medium text-gray-700">Include payroll</span>
          </label>
        </div>

        {includeEmployer && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employer Name</label>
              <input
                type="text"
                value={employerSchedule.employerName}
                onChange={(e) => setEmployerSchedule({ ...employerSchedule, employerName: e.target.value })}
                placeholder="Acme Corporation"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Net Pay Amount</label>
              <input
                type="number"
                step="0.01"
                value={employerSchedule.baseNetPay}
                onChange={(e) => setEmployerSchedule({ ...employerSchedule, baseNetPay: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Frequency</label>
              <select
                value={employerSchedule.payFrequency}
                onChange={(e) => setEmployerSchedule({ ...employerSchedule, payFrequency: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="WEEKLY">Weekly</option>
                <option value="BIWEEKLY">Biweekly</option>
                <option value="SEMIMONTHLY">Semi-Monthly</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Pay Date</label>
              <input
                type="date"
                value={employerSchedule.firstPayDate}
                onChange={(e) => setEmployerSchedule({ ...employerSchedule, firstPayDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Recurring Payments (Optional) */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">5. Recurring Payments (Optional)</h2>
          <button
            type="button"
            onClick={addRecurringPayment}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Payment
          </button>
        </div>

        {recurringPayments.length === 0 ? (
          <p className="text-gray-500 text-sm">No recurring payments added. Click "Add Payment" to add rent, mortgage, car payments, etc.</p>
        ) : (
          <div className="space-y-4">
            {recurringPayments.map((payment, index) => (
              <div key={payment.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">Payment #{index + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeRecurringPayment(payment.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Payment Type</label>
                    <select
                      value={payment.paymentType}
                      onChange={(e) => updateRecurringPayment(payment.id, 'paymentType', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {PAYMENT_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="lg:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Payee Name</label>
                    <input
                      type="text"
                      value={payment.payeeName}
                      onChange={(e) => updateRecurringPayment(payment.id, 'payeeName', e.target.value)}
                      placeholder="Enter payee name"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={payment.amount}
                      onChange={(e) => updateRecurringPayment(payment.id, 'amount', parseFloat(e.target.value))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
                    <select
                      value={payment.frequency}
                      onChange={(e) => updateRecurringPayment(payment.id, 'frequency', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="WEEKLY">Weekly</option>
                      <option value="BIWEEKLY">Biweekly</option>
                      <option value="MONTHLY">Monthly</option>
                    </select>
                  </div>
                </div>
                {payment.frequency === 'MONTHLY' && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Day of Month</label>
                    <select
                      value={payment.dayOfMonth}
                      onChange={(e) => updateRecurringPayment(payment.id, 'dayOfMonth', parseInt(e.target.value))}
                      className="w-32 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Output Formats */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">6. Output Formats</h2>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formats.pdf}
              onChange={(e) => setFormats({ ...formats, pdf: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">PDF</div>
              <div className="text-sm text-gray-500">Print-ready PDF document</div>
            </div>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formats.html}
              onChange={(e) => setFormats({ ...formats, html: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">HTML</div>
              <div className="text-sm text-gray-500">Web-viewable format</div>
            </div>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formats.excel}
              onChange={(e) => setFormats({ ...formats, excel: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">Excel</div>
              <div className="text-sm text-gray-500">Editable spreadsheet</div>
            </div>
          </label>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={generating || !selectedTemplate}
        className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-colors ${
          generating || !selectedTemplate
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {generating ? 'Generating Statement...' : 'Generate Statement'}
      </button>
    </form>
  );
};
